/**
 * Wire contract between the assistant UI and the AI service (kothar-ai-backend).
 *
 * POST {REACT_APP_AI_API_URL}/agent/chat   -> text/event-stream
 *   body: { conversationId?, surface: "admin" | "partner", message, context: { path } }
 *   events (each `event:` line names one, `data:` is JSON):
 *     conversation { id }                         the id to send back on the next turn
 *     text         { delta }                      a piece of the assistant's reply
 *     tool_start   { id, name, label }            the agent began a lookup or action
 *     tool_end     { id, ok }                     that step finished
 *     proposal     { id, kind, title, summary, fields: [{ label, value }] }
 *                                                 a change the agent wants to make; nothing is
 *                                                 saved until the user approves it
 *     error        { message }
 *     done         {}
 * POST /agent/proposals/{id}/approve | /reject  -> { status, message? }
 */

/**
 * Splits a growing SSE buffer into complete events. Returns the parsed events and whatever
 * trailing text has not been terminated by a blank line yet, to prepend to the next chunk.
 */
export const parseSseChunk = (buffer) => {
  const blocks = buffer.replace(/\r\n/g, "\n").split("\n\n");
  const rest = blocks.pop();
  const events = blocks
    .map((block) => {
      let type = "message";
      const dataLines = [];
      block.split("\n").forEach((line) => {
        if (line.startsWith("event:")) type = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
      });
      if (!dataLines.length) return null;
      try {
        return { type, data: JSON.parse(dataLines.join("\n")) };
      } catch {
        return null;
      }
    })
    .filter(Boolean);
  return { events, rest };
};

let localId = 0;
export const nextLocalId = (prefix) => `${prefix}-${Date.now()}-${localId++}`;

export const initialAssistantState = {
  conversationId: null,
  messages: [],
  status: "idle", // idle | streaming
};

/** The assistant message currently being written: the last one, if it is still streaming. */
const updateStreaming = (state, update) => {
  const messages = [...state.messages];
  const index = messages.length - 1;
  const last = messages[index];
  if (!last || last.role !== "assistant" || !last.streaming) return state;
  messages[index] = update(last);
  return { ...state, messages };
};

export const assistantReducer = (state, action) => {
  switch (action.type) {
    case "send":
      return {
        ...state,
        status: "streaming",
        messages: [
          ...state.messages,
          { id: nextLocalId("user"), role: "user", text: action.text },
          {
            id: nextLocalId("assistant"),
            role: "assistant",
            text: "",
            steps: [],
            proposals: [],
            streaming: true,
          },
        ],
      };
    case "event": {
      const { type, data } = action.event;
      if (type === "conversation") return { ...state, conversationId: data.id };
      if (type === "text") {
        return updateStreaming(state, (m) => ({ ...m, text: m.text + (data.delta || "") }));
      }
      if (type === "tool_start") {
        return updateStreaming(state, (m) => ({
          ...m,
          steps: [...m.steps, { id: data.id, label: data.label || data.name, state: "running" }],
        }));
      }
      if (type === "tool_end") {
        return updateStreaming(state, (m) => ({
          ...m,
          steps: m.steps.map((step) =>
            step.id === data.id ? { ...step, state: data.ok === false ? "failed" : "done" } : step
          ),
        }));
      }
      if (type === "proposal") {
        return updateStreaming(state, (m) => ({
          ...m,
          proposals: [...m.proposals, { ...data, status: "pending" }],
        }));
      }
      if (type === "error") {
        return updateStreaming(state, (m) => ({ ...m, error: data.message || "Something failed" }));
      }
      return state;
    }
    case "finish":
      return {
        ...updateStreaming(state, (m) => ({
          ...m,
          streaming: false,
          steps: m.steps.map((step) =>
            step.state === "running" ? { ...step, state: "done" } : step
          ),
          error: action.error || m.error,
        })),
        status: "idle",
      };
    case "proposal":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.proposals?.some((p) => p.id === action.id)
            ? {
                ...m,
                proposals: m.proposals.map((p) =>
                  p.id === action.id ? { ...p, status: action.status, note: action.note } : p
                ),
              }
            : m
        ),
      };
    case "reset":
      return initialAssistantState;
    default:
      return state;
  }
};

/**
 * Splits a reply into blocks for rendering without raw HTML: paragraphs, bullet lists and
 * **bold** runs. Anything else stays plain text.
 */
export const formatReply = (text = "") =>
  text
    .trim()
    .split(/\n{2,}/)
    .filter(Boolean)
    .map((block) => {
      const lines = block.split("\n");
      const isList = lines.every((line) => /^\s*([-*•]|\d+\.)\s+/.test(line));
      return isList
        ? { type: "list", items: lines.map((line) => line.replace(/^\s*([-*•]|\d+\.)\s+/, "")) }
        : { type: "paragraph", text: block };
    });

/** Splits a line into plain and bold runs on **double asterisks**. */
export const boldRuns = (line = "") =>
  line
    .split(/(\*\*[^*]+\*\*)/)
    .filter(Boolean)
    .map((part) =>
      part.startsWith("**") && part.endsWith("**")
        ? { bold: true, text: part.slice(2, -2) }
        : { bold: false, text: part }
    );
